import { ContasPagarService } from '../../../shared/services/api/Financeiro/ContasPagar';
import { EmpresaService } from '../../../shared/services/api/cadastros/Empresa';
import { IComprovanteBaixaDados } from '../ContasReceber/ComprovanteBaixa';

// Dados da conta que a tela já tem — usados no recibo de conta ainda não
// paga (e como reserva quando o pagamento não traz nome/número).
export interface IContaParaRecibo {
  idpagar: string;
  parceiro: string;
  documentoParceiro?: string;
  numero?: string;
  parcela?: string;
  historico?: string;
  valorEmAberto: number; // saldo a pagar (recibo antes da baixa)
}

// Forma de pagamento = começo do histórico do caixa ("PIX - contas 123" → "PIX").
const nomeForma = (historico: string) => (historico || '').split(' - contas ')[0].split(' — ')[0].trim() || 'Pagamento';

// Recibo de pagamento pro parceiro assinar (Contas a Pagar — listagem e
// detalhe). Conta já paga (total ou parcial): valor, formas e contas de saída
// dos pagamentos feitos. Conta ainda não paga: o saldo em aberto, com a data
// de hoje e sem forma de pagamento — pra assinar na hora de entregar o valor.
export const montarReciboPagamento = async (conta: IContaParaRecibo): Promise<IComprovanteBaixaDados | Error> => {
  const [pagas, empresaResult] = await Promise.all([
    ContasPagarService.getPagas(conta.idpagar),
    EmpresaService.getById(),
  ]);
  if (pagas instanceof Error) return pagas;
  const empresa = !(empresaResult instanceof Error) ? empresaResult : undefined;
  const base = {
    tipo: 'pagamento' as const,
    documentoParceiro: conta.documentoParceiro,
    desconto: 0,
    acrescimo: 0,
    empresaFantasia: empresa?.fantasia,
    empresaRazaoSocial: empresa?.nome,
  };

  if (pagas.length === 0) {
    const valor = Number(conta.valorEmAberto || 0);
    if (!(valor > 0)) return new Error('Esta conta não tem valor a pagar nem pagamento registrado.');
    return {
      ...base,
      parceiro: conta.parceiro || '-',
      contaNome: '-',
      datalan: new Date(),
      contas: [{ numero: conta.numero || '', parcela: conta.parcela || undefined, valor, historico: conta.historico || undefined }],
      formasPagamento: [],
      totalBaixado: valor,
    };
  }

  const formas = pagas.flatMap((p) => (p.formasPagamento.length > 0
    ? p.formasPagamento.map((f) => ({ nome: `${nomeForma(f.historico)}${f.nomeConta ? ` (${f.nomeConta.trim()})` : ''}`, valor: Number(f.valor) }))
    : [{ nome: `Pagamento${p.nomeConta ? ` (${p.nomeConta.trim()})` : ''}`, valor: Number(p.valor) }]));
  const ultimo = pagas[pagas.length - 1];
  const total = pagas.reduce((s, p) => s + Number(p.valor), 0);
  return {
    ...base,
    parceiro: ultimo.nomeParceiro || conta.parceiro || '-',
    contaNome: [...new Set(pagas.map((p) => (p.nomeConta || '').trim()).filter(Boolean))].join(', ') || '-',
    datalan: ultimo.datalan,
    contas: [{
      numero: ultimo.numero || conta.numero || '',
      parcela: ultimo.parcela || conta.parcela || undefined,
      valor: total,
      historico: ultimo.historico || conta.historico || undefined,
    }],
    formasPagamento: formas,
    totalBaixado: total,
  };
};

// Recibo de todas as parcelas do lançamento (um por parcela, na ordem) —
// opção "Todas as parcelas" do visualizador. Parcela sem valor nem
// pagamento fica de fora.
export const montarRecibosDasParcelas = async (
  parcelas: { idpagar: string; numero?: string; parcela?: string; vparcela: number }[],
  comum: { parceiro: string; documentoParceiro?: string; historico?: string },
): Promise<IComprovanteBaixaDados[] | Error> => {
  const recibos = await Promise.all(parcelas.map((p) => montarReciboPagamento({
    idpagar: p.idpagar,
    parceiro: comum.parceiro,
    documentoParceiro: comum.documentoParceiro,
    numero: p.numero,
    parcela: p.parcela,
    historico: comum.historico,
    valorEmAberto: Number(p.vparcela),
  })));
  const validos = recibos.filter((r): r is IComprovanteBaixaDados => !(r instanceof Error));
  if (validos.length === 0) return new Error('Nenhuma parcela com valor para gerar recibo.');
  return validos;
};
